// src/app/_module/hooks/use-notification-sound.tsx
"use client";

import { useEffect, useRef, useCallback } from "react";

// Notification sound file path (Slack-style two-note ascending chime)
const NOTIFICATION_SOUND_URL = "/sounds/notification.wav";

interface UseNotificationSoundOptions {
  enabled?: boolean;
  volume?: number; // 0 to 1
}

export function useNotificationSound(options: UseNotificationSoundOptions = {}) {
  const { enabled = true, volume = 0.5 } = options;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasInteractedRef = useRef(false);

  // Initialize audio element
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Create audio element
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.preload = 'auto';
    audioRef.current = audio;

    // Track user interaction to comply with browser autoplay policies
    const handleInteraction = () => {
      hasInteractedRef.current = true;
    };

    // Listen for any user interaction
    window.addEventListener('click', handleInteraction, { once: true });
    window.addEventListener('keydown', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [volume]);

  const playSound = useCallback(() => {
    if (!enabled || !audioRef.current) {
      return;
    }

    // Only play if user has interacted with the page (browser autoplay policy)
    if (!hasInteractedRef.current) {

      return;
    }

    // Reset and play the sound
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch((error) => {
      // Silently fail if autoplay is blocked

    });
  }, [enabled]);

  return { playSound };
}

// Custom hook to manage notification sound preference from localStorage
export function useNotificationSoundPreference() {
  const STORAGE_KEY = 'notificationSoundEnabled';

  const getSoundPreference = useCallback((): boolean => {
    if (typeof window === 'undefined') return true;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  }, []);

  const setSoundPreference = useCallback((enabled: boolean) => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, String(enabled));
      // Dispatch custom event so other components can react
      window.dispatchEvent(new CustomEvent('notificationSoundPreferenceChanged', {
        detail: { enabled }
      }));
    } catch (error) {

    }
  }, []);

  return { getSoundPreference, setSoundPreference };
}
